import { useEffect, useState, useCallback } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import api from '../services/api'
import DicomViewer from '../components/DicomViewer'
import ReconstructPanel from '../components/ReconstructPanel'
import SegmentPanel from '../components/SegmentPanel'

function formatSize(bytes) {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${bytes} B`
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

export default function ReviewPage() {
  const { caseId } = useParams()
  const [searchParams] = useSearchParams()
  const [caseSummary, setCaseSummary] = useState(null)
  const [seriesList, setSeriesList] = useState(null)
  const [selectedSeries, setSelectedSeries] = useState(null)
  const [dicomFiles, setDicomFiles] = useState(null)
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('Loading case...')
  const [error, setError] = useState(null)
  const [showReconstruct, setShowReconstruct] = useState(false)
  const [reconstructOnly, setReconstructOnly] = useState(false) // skip viewer, go straight to 3D
  const [showSegment, setShowSegment] = useState(false)
  const [segmentOnly, setSegmentOnly] = useState(false) // skip viewer, go straight to AI segment
  const [qualityMap, setQualityMap] = useState({}) // seriesId → quality data
  const [expandedQuality, setExpandedQuality] = useState(null) // seriesId to show full report
  // Accept token from portal via URL param on first load
  useEffect(() => {
    const token = searchParams.get('token')
    if (token) {
      sessionStorage.setItem('adminToken', token)
      window.history.replaceState({}, '', `/review/${caseId}`)
    }
  }, [searchParams, caseId])

  // Fetch case summary + series list
  useEffect(() => {
    async function fetchSeriesList() {
      try {
        setLoading(true)
        setStatus('Fetching case info...')

        const [summaryRes, seriesRes] = await Promise.all([
          api.get(`/studio/${caseId}/summary`),
          api.get(`/studio/${caseId}/series`),
        ])
        setCaseSummary(summaryRes.data)
        setSeriesList(seriesRes.data.series)
        setLoading(false)
      } catch (err) {
        setError(err.response?.data?.error || err.message)
        setLoading(false)
      }
    }
    fetchSeriesList()
  }, [caseId])

  // Fetch quality analysis for each non-archive series (fire-and-forget per series)
  useEffect(() => {
    if (!seriesList || seriesList.length === 0) return
    for (const s of seriesList) {
      if (s.type === 'archive' || qualityMap[s.id]) continue
      api.get(`/studio/${caseId}/series/${s.id}/quality`)
        .then((res) => {
          if (res.data.quality) {
            setQualityMap((prev) => ({ ...prev, [s.id]: res.data.quality }))
          }
        })
        .catch(() => {}) // quality is best-effort, don't block UI
    }
  }, [seriesList, caseId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Extract a ZIP archive server-side, then reload series list
  const extractArchive = useCallback(async (series) => {
    try {
      setLoading(true)
      setError(null)
      setStatus(`Extracting ${series.name} on server (this may take a minute)...`)

      const res = await api.post(`/studio/${caseId}/extract/${series.id}`)
      const { folderName, filesExtracted } = res.data

      setStatus(`Extracted ${filesExtracted} DICOM files into ${folderName}. Refreshing...`)

      // Refresh series list — the ZIP is still there but there's now a folder too
      const seriesRes = await api.get(`/studio/${caseId}/series`)
      setSeriesList(seriesRes.data.series)
      setSelectedSeries(null)
      setDicomFiles(null)
      setLoading(false)
    } catch (err) {
      setError(err.response?.data?.error || err.message)
      setLoading(false)
    }
  }, [caseId])

  // Load files for a folder series (individual DICOM files streamed from Drive)
  const loadSeries = useCallback(async (series) => {
    try {
      // If it's an archive at the top level, extract it server-side first
      if (series.type === 'archive') {
        await extractArchive(series)
        return
      }

      setSelectedSeries(series)
      setDicomFiles(null)
      setLoading(true)
      setError(null)
      setStatus(`Loading ${series.name}...`)

      const filesRes = await api.get(`/studio/${caseId}/series/${series.id}/files`)
      const allFiles = filesRes.data.files
      const rawDicomFiles = allFiles.filter((f) => f.type === 'dicom')
      const zipFiles = allFiles.filter((f) => f.type === 'zip' || f.type === 'rar')

      // If folder contains only ZIPs (no loose DICOM), extract the first ZIP
      if (rawDicomFiles.length === 0 && zipFiles.length > 0) {
        setStatus(`Found ${zipFiles.length} archive(s) inside ${series.name}. Extracting...`)
        await extractArchive({ id: zipFiles[0].id, name: zipFiles[0].name, type: 'archive' })
        return
      }

      if (rawDicomFiles.length === 0) {
        setDicomFiles([])
        setLoading(false)
        return
      }

      const processedFiles = rawDicomFiles.map((f) => ({
        name: f.name,
        type: 'url',
        url: `${import.meta.env.VITE_API_BASE_URL || ''}/api/studio/${caseId}/files/${f.id}`,
      }))

      processedFiles.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true })
      )

      setDicomFiles(processedFiles)
      setLoading(false)
    } catch (err) {
      setError(err.response?.data?.error || err.message)
      setLoading(false)
    }
  }, [caseId, extractArchive])

  // Go straight to 3D reconstruction without loading the viewer
  const startReconstructDirect = (series) => {
    if (series.type === 'archive') {
      loadSeries(series)
      return
    }
    setSelectedSeries(series)
    setDicomFiles(null)
    setReconstructOnly(true)
    setShowReconstruct(true)
    setLoading(false)
  }

  // Go straight to AI segmentation without loading the viewer
  const startSegmentDirect = (series) => {
    if (series.type === 'archive') {
      loadSeries(series)
      return
    }
    setSelectedSeries(series)
    setDicomFiles(null)
    setSegmentOnly(true)
    setShowSegment(true)
    setLoading(false)
  }

  const goBackToSeriesList = () => {
    setSelectedSeries(null)
    setDicomFiles(null)
    setReconstructOnly(false)
    setShowReconstruct(false)
    setSegmentOnly(false)
    setShowSegment(false)
    setError(null)
  }

  // --- Loading state ---
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-400">{status}</p>
        </div>
      </div>
    )
  }

  // --- Error state ---
  if (error) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-900">
        <div className="card max-w-md text-center">
          <p className="text-red-400 font-medium text-lg">Error</p>
          <p className="text-gray-400 mt-2">{error}</p>
          <div className="flex gap-3 justify-center mt-4">
            {selectedSeries && (
              <button onClick={goBackToSeriesList} className="btn-secondary">
                Back to series
              </button>
            )}
            <button onClick={() => window.close()} className="btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  // --- Series picker (no series selected yet) ---
  if (!selectedSeries) {
    return (
      <div className="h-screen flex flex-col bg-gray-900">
        <header className="flex items-center gap-4 px-6 py-4 bg-gray-800 border-b border-gray-700">
          <img src="/osteo3d-logo-white.png" alt="Osteo3D" className="h-8" />
          <div>
            <h1 className="text-lg font-semibold text-white">
              Studio — {caseId}
            </h1>
            {caseSummary && (
              <p className="text-xs text-gray-400">
                {caseSummary.notes || 'No notes'}
              </p>
            )}
          </div>
        </header>

        <div className="flex-1 flex items-center justify-center p-6">
          {!seriesList || seriesList.length === 0 ? (
            <div className="card max-w-md text-center">
              <p className="text-gray-300 font-medium text-lg">No DICOM data</p>
              <p className="text-gray-500 mt-2">
                Case {caseId} has no DICOM files or archives in Drive yet.
              </p>
            </div>
          ) : (
            <div className="w-full max-w-lg">
              <h2 className="text-white text-lg font-medium mb-4">
                Select a series to review
              </h2>
              <div className="space-y-3">
                {seriesList.map((s) => {
                  const q = qualityMap[s.id]
                  const gradeColors = {
                    Excellent: 'bg-green-900/50 text-green-400 border-green-700',
                    Good: 'bg-blue-900/50 text-blue-400 border-blue-700',
                    Fair: 'bg-yellow-900/50 text-yellow-400 border-yellow-700',
                    Poor: 'bg-red-900/50 text-red-400 border-red-700',
                  }
                  return (
                  <div
                    key={s.id}
                    className="bg-gray-800 border border-gray-700 rounded-xl px-5 py-4"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">
                          {s.name}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {s.type === 'archive' ? (
                            <>Archive · {formatSize(s.totalSize)}</>
                          ) : (
                            <>
                              {s.dicomCount > 0 && `${s.dicomCount} DICOM files`}
                              {s.dicomCount > 0 && s.zipCount > 0 && ' + '}
                              {s.zipCount > 0 && `${s.zipCount} archive${s.zipCount > 1 ? 's' : ''}`}
                              {' · '}{formatSize(s.totalSize)}
                            </>
                          )}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">
                          {formatDate(s.createdTime)}
                        </p>
                        <span className={`inline-block mt-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${
                          s.type === 'archive'
                            ? 'bg-blue-900/50 text-blue-400'
                            : 'bg-green-900/50 text-green-400'
                        }`}>
                          {s.type === 'archive' ? 'ZIP' : 'Folder'}
                        </span>
                      </div>
                    </div>

                    {/* Quality report — shown once header analysis completes */}
                    {q && (() => {
                      const isDetailOpen = expandedQuality === s.id
                      const scorePercent = Math.round((q.printabilityScore / 6) * 100)
                      const barColor = q.printabilityGrade === 'Excellent' ? 'bg-green-500' : q.printabilityGrade === 'Good' ? 'bg-blue-500' : q.printabilityGrade === 'Fair' ? 'bg-yellow-500' : 'bg-red-500'
                      return (
                      <div className="mt-3 space-y-2">
                        {/* Score bar + grade + toggle */}
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${scorePercent}%` }} />
                          </div>
                          <span className={`text-xs font-semibold ${gradeColors[q.printabilityGrade]?.replace('border-', 'text-').split(' ').find(c => c.startsWith('text-')) || 'text-gray-400'}`}>
                            {q.printabilityGrade} ({q.printabilityScore}/6)
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); setExpandedQuality(isDetailOpen ? null : s.id) }}
                            className="text-gray-500 hover:text-gray-300 transition-colors"
                            title={isDetailOpen ? 'Hide details' : 'Show details'}
                          >
                            <svg className={`w-4 h-4 transition-transform ${isDetailOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        </div>

                        {/* Tag pills */}
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-700 text-gray-300">
                            {q.seriesType}
                          </span>
                          {q.modality && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-700 text-gray-300">
                              {q.modality}
                            </span>
                          )}
                          {q.sliceThickness && (
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                              q.sliceThickness <= 1 ? 'bg-green-900/50 text-green-400' : q.sliceThickness <= 2 ? 'bg-gray-700 text-gray-300' : 'bg-yellow-900/50 text-yellow-400'
                            }`}>
                              {q.sliceThickness}mm slices
                            </span>
                          )}
                          {q.kernel && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-700 text-gray-300">
                              {q.kernel}
                            </span>
                          )}
                          {q.contrast && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-900/50 text-orange-400 border border-orange-700">
                              Contrast
                            </span>
                          )}
                          {q.gantryTilt !== 0 && Math.abs(q.gantryTilt) >= 1 && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-900/50 text-yellow-400 border border-yellow-700">
                              Tilt {q.gantryTilt.toFixed(1)}&deg;
                            </span>
                          )}
                          {q.sliceCount && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-700 text-gray-300">
                              {q.sliceCount} slices
                            </span>
                          )}
                        </div>

                        {/* Expanded detail table */}
                        {isDetailOpen && (
                          <div className="mt-2 bg-gray-900/50 rounded-lg p-3 space-y-2 border border-gray-700/50">
                            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Scan Quality Report</p>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px]">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Modality</span>
                                <span className={`font-medium ${q.modality === 'CT' ? 'text-green-400' : 'text-gray-300'}`}>{q.modality || '—'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Series Type</span>
                                <span className="text-gray-300 font-medium">{q.seriesType || '—'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Slice Thickness</span>
                                <span className={`font-medium ${q.sliceThickness <= 1 ? 'text-green-400' : q.sliceThickness <= 2 ? 'text-yellow-400' : 'text-red-400'}`}>{q.sliceThickness ? `${q.sliceThickness} mm` : '—'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Pixel Spacing</span>
                                <span className="text-gray-300 font-medium">{q.pixelSpacing ? `${q.pixelSpacing} mm` : '—'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Kernel</span>
                                <span className="text-gray-300 font-medium">{q.kernel || '—'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Slice Count</span>
                                <span className={`font-medium ${q.sliceCount >= 100 ? 'text-green-400' : 'text-yellow-400'}`}>{q.sliceCount || '—'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Contrast Agent</span>
                                <span className={`font-medium ${q.contrast ? 'text-orange-400' : 'text-green-400'}`}>{q.contrast ? 'Yes' : 'No'}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Gantry Tilt</span>
                                <span className={`font-medium ${Math.abs(q.gantryTilt || 0) >= 1 ? 'text-yellow-400' : 'text-green-400'}`}>{q.gantryTilt ? `${q.gantryTilt.toFixed(1)}\u00b0` : '0\u00b0'}</span>
                              </div>
                            </div>
                            {/* Warnings */}
                            {q.warnings?.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-gray-700/50 space-y-1">
                                {q.warnings.map((w, i) => (
                                  <p key={i} className="text-[10px] text-yellow-400/80 leading-tight flex items-start gap-1.5">
                                    <span className="flex-shrink-0 w-3.5 h-3.5 rounded-full bg-yellow-900/50 flex items-center justify-center text-[8px] mt-0.5">!</span>
                                    {w}
                                  </p>
                                ))}
                              </div>
                            )}
                            {/* Recommendations */}
                            {q.recommendations?.length > 0 && (
                              <div className="mt-1 space-y-1">
                                {q.recommendations.map((r, i) => (
                                  <p key={i} className="text-[10px] text-blue-400/80 leading-tight flex items-start gap-1.5">
                                    <span className="flex-shrink-0 w-3.5 h-3.5 rounded-full bg-blue-900/50 flex items-center justify-center text-[8px] mt-0.5">i</span>
                                    {r}
                                  </p>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Warnings (collapsed view) */}
                        {!isDetailOpen && q.warnings?.length > 0 && (
                          <div className="space-y-0.5">
                            {q.warnings.slice(0, 2).map((w, i) => (
                              <p key={i} className="text-[10px] text-yellow-400/80 leading-tight">&#9888; {w}</p>
                            ))}
                            {q.warnings.length > 2 && (
                              <button onClick={() => setExpandedQuality(s.id)} className="text-[10px] text-gray-500 hover:text-gray-300">
                                +{q.warnings.length - 2} more...
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      )
                    })()}
                    {!q && s.type !== 'archive' && (
                      <div className="mt-3">
                        <span className="text-[10px] text-gray-500 animate-pulse">Analyzing...</span>
                      </div>
                    )}

                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => loadSeries(s)}
                        className="flex-1 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 text-sm font-medium rounded-lg transition-colors"
                      >
                        {s.type === 'archive' ? 'Extract Archive' : 'View DICOM'}
                      </button>
                      {s.type !== 'archive' && (
                        <button
                          onClick={() => startReconstructDirect(s)}
                          className="flex-1 px-3 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          3D Reconstruct
                        </button>
                      )}
                      {s.type !== 'archive' && (
                        <button
                          onClick={() => startSegmentDirect(s)}
                          className="flex-1 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          AI Segment
                        </button>
                      )}
                    </div>
                  </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  // --- Reconstruct-only mode (skip viewer) ---
  if (reconstructOnly && selectedSeries) {
    return (
      <div className="h-screen flex flex-col bg-gray-900">
        <header className="flex items-center gap-4 px-4 py-3 bg-gray-800 border-b border-gray-700">
          <button
            onClick={goBackToSeriesList}
            className="text-gray-400 hover:text-white transition-colors p-1"
            title="Back to series list"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <img src="/osteo3d-logo-white.png" alt="Osteo3D" className="h-8" />
          <div>
            <h1 className="text-lg font-semibold text-white">
              {caseId} — {selectedSeries.name}
            </h1>
            <p className="text-xs text-gray-400">3D Reconstruction</p>
          </div>
        </header>

        <div className="flex-1 flex items-center justify-center relative">
          <div className="text-center text-gray-500">
            <p className="text-lg font-medium text-gray-400">3D Reconstruction Mode</p>
            <p className="text-sm mt-1">Use the panel on the right to configure and generate your model</p>
          </div>
          <ReconstructPanel
            caseId={caseId}
            seriesId={selectedSeries.id}
            onClose={goBackToSeriesList}
            headerQuality={qualityMap[selectedSeries.id] || null}
          />
        </div>
      </div>
    )
  }

  // --- Segment-only mode (skip viewer, go straight to AI segmentation) ---
  if (segmentOnly && selectedSeries) {
    return (
      <div className="h-screen flex flex-col bg-gray-900">
        <header className="flex items-center gap-4 px-4 py-3 bg-gray-800 border-b border-gray-700">
          <button
            onClick={goBackToSeriesList}
            className="text-gray-400 hover:text-white transition-colors p-1"
            title="Back to series list"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <img src="/osteo3d-logo-white.png" alt="Osteo3D" className="h-8" />
          <div>
            <h1 className="text-lg font-semibold text-white">
              {caseId} — {selectedSeries.name}
            </h1>
            <p className="text-xs text-gray-400">AI Segmentation</p>
          </div>
        </header>

        <div className="flex-1 flex items-center justify-center relative">
          <div className="text-center text-gray-500">
            <p className="text-lg font-medium text-gray-400">AI Segmentation Mode</p>
            <p className="text-sm mt-1">Select anatomical structures in the panel on the right</p>
          </div>
          <SegmentPanel
            caseId={caseId}
            seriesId={selectedSeries.id}
            onClose={goBackToSeriesList}
          />
        </div>
      </div>
    )
  }

  // --- Viewer (series selected, files loaded) ---
  if (!dicomFiles || dicomFiles.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-900">
        <div className="card max-w-md text-center">
          <p className="text-gray-300 font-medium text-lg">No DICOM files</p>
          <p className="text-gray-500 mt-2">
            {selectedSeries.name} contains no DICOM files.
          </p>
          <button onClick={goBackToSeriesList} className="btn-secondary mt-4">
            Back to series
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen flex flex-col bg-gray-900">
      <header className="flex items-center justify-between px-4 py-3 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-4">
          <button
            onClick={goBackToSeriesList}
            className="text-gray-400 hover:text-white transition-colors p-1"
            title="Back to series list"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <img src="/osteo3d-logo-white.png" alt="Osteo3D" className="h-8" />
          <div>
            <h1 className="text-lg font-semibold text-white">
              {caseId} — {selectedSeries.name}
            </h1>
            <p className="text-xs text-gray-400">
              {dicomFiles.length} DICOM slices
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { setShowSegment(!showSegment); if (!showSegment) setShowReconstruct(false) }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              showSegment
                ? 'bg-purple-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
            title="AI anatomical segmentation"
          >
            AI Segment
          </button>
          <button
            onClick={() => { setShowReconstruct(!showReconstruct); if (!showReconstruct) setShowSegment(false) }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              showReconstruct
                ? 'bg-brand-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
            title="Generate 3D model from DICOM"
          >
            3D Reconstruct
          </button>
        </div>
      </header>

      <div className="flex-1 min-h-0 relative">
        <DicomViewer dicomFiles={dicomFiles} />
        {showReconstruct && (
          <ReconstructPanel
            caseId={caseId}
            seriesId={selectedSeries.id}
            onClose={() => setShowReconstruct(false)}
            headerQuality={qualityMap[selectedSeries.id] || null}
          />
        )}
        {showSegment && (
          <SegmentPanel
            caseId={caseId}
            seriesId={selectedSeries.id}
            onClose={() => setShowSegment(false)}
          />
        )}
      </div>
    </div>
  )
}
